import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Download,
  Save,
  X,
  FileCheck,
  RefreshCw,
  Search,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GlossaryAdmin = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user has @yomev.org email
  const isYomevUser = user?.email?.toLowerCase().endsWith('@yomev.org');
  
  const [glossary, setGlossary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const [formData, setFormData] = useState({
    preferredTerm: '',
    synonyms: '',
    bannedTerms: '',
    definition: '',
    category: 'genel'
  });

  const categoryOptions = [
    { value: 'genel', label: 'Genel', color: 'bg-gray-100 text-gray-700' },
    { value: 'eğitim', label: 'Eğitim', color: 'bg-blue-100 text-blue-700' },
    { value: 'gelişim', label: 'Gelişim', color: 'bg-purple-100 text-purple-700' },
    { value: 'metodoloji', label: 'Metodoloji', color: 'bg-green-100 text-green-700' }
  ];

  useEffect(() => {
    if (!isYomevUser) {
      navigate('/plan-studio');
      return;
    }
  }, [isYomevUser, navigate]);

  useEffect(() => {
    fetchGlossary();
  }, [filterCategory, searchQuery]);

  const fetchGlossary = async () => {
    try {
      let url = `${API}/api/plan-studio/glossary`;
      const params = [];
      if (filterCategory) params.push(`category=${filterCategory}`);
      if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setGlossary(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        preferredTerm: formData.preferredTerm,
        synonyms: formData.synonyms.split(',').map(s => s.trim()).filter(Boolean),
        bannedTerms: formData.bannedTerms.split(',').map(s => s.trim()).filter(Boolean),
        definition: formData.definition,
        category: formData.category
      };

      const res = await fetch(`${API}/api/plan-studio/glossary`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        await fetchGlossary();
        setShowModal(false);
        resetForm();
      } else {
        const err = await res.json();
        alert(err.detail || 'Kaydetme hatası');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/api/plan-studio/glossary/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const result = await res.json();
        alert(`${result.importedCount} terim içe aktarıldı`);
        await fetchGlossary();
        setShowImportModal(false);
      } else {
        const err = await res.json();
        alert(err.detail || 'İçe aktarma hatası');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const resetForm = () => {
    setFormData({
      preferredTerm: '',
      synonyms: '',
      bannedTerms: '',
      definition: '',
      category: 'genel'
    });
    setEditingId(null);
  };

  const exportToCSV = () => {
    const headers = ['preferredTerm', 'synonyms', 'bannedTerms', 'definition', 'category'];
    const rows = glossary.map(t => [
      t.preferredTerm || '',
      (t.synonyms || []).join('; '),
      (t.bannedTerms || []).join('; '),
      t.definition || '',
      t.category || 'genel'
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'terim_sozlugu.csv';
    link.click();
  };

  const openEditModal = (term) => {
    setFormData({
      preferredTerm: term.preferredTerm,
      synonyms: (term.synonyms || []).join(', '),
      bannedTerms: (term.bannedTerms || []).join(', '),
      definition: term.definition || '',
      category: term.category || 'genel'
    });
    setEditingId(term.id);
    setShowModal(true);
  };

  const getCategoryBadge = (category) => {
    const opt = categoryOptions.find(c => c.value === category) || categoryOptions[0];
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${opt.color}`}>{opt.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="glossary-admin">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link
            to="/plan-studio"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft size={20} />
            Plan Studio'ya Dön
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileCheck className="w-8 h-8 text-amber-600" />
            Terim Sözlüğü
          </h1>
          <p className="text-gray-600 mt-1">Türkçe terminoloji standardı ve yasaklı terimler</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            data-testid="import-csv-btn"
          >
            <Upload size={18} />
            CSV İçe Aktar
          </button>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            data-testid="export-csv-btn"
          >
            <Download size={18} />
            CSV Dışa Aktar
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
            data-testid="new-term-btn"
          >
            <Plus size={18} />
            Yeni Terim
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">Terim Sözlüğü Nasıl Çalışır?</h4>
            <p className="text-sm text-amber-700 mt-1">
              AI plan oluştururken bu sözlükteki "Yasaklı Terimler"i kullanmaz ve bunları "Tercih Edilen Terim" ile değiştirir.
              Örneğin: "Activity" → "Etkinlik", "Development" → "Gelişim" gibi.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Terim ara..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>
          <div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Tüm Kategoriler</option>
              {categoryOptions.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500">
            {glossary.length} terim
          </div>
        </div>
      </div>

      {/* Glossary Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tercih Edilen Terim</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yasaklı Terimler</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Eş Anlamlılar</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategori</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {glossary.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <FileCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Henüz terim eklenmemiş</p>
                </td>
              </tr>
            ) : (
              glossary.map((term) => (
                <tr key={term.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-500" />
                      <span className="font-medium text-gray-900">{term.preferredTerm}</span>
                    </div>
                    {term.definition && (
                      <p className="text-xs text-gray-500 mt-1 max-w-xs truncate">{term.definition}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {term.bannedTerms?.map((banned, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs line-through">
                          {banned}
                        </span>
                      ))}
                      {(!term.bannedTerms || term.bannedTerms.length === 0) && (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {term.synonyms?.map((syn, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                          {syn}
                        </span>
                      ))}
                      {(!term.synonyms || term.synonyms.length === 0) && (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getCategoryBadge(term.category)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openEditModal(term)}
                      className="text-amber-600 hover:text-amber-700"
                    >
                      <Edit2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Terimi Düzenle' : 'Yeni Terim Ekle'}
              </h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tercih Edilen Terim (Türkçe) *
                </label>
                <input
                  type="text"
                  value={formData.preferredTerm}
                  onChange={(e) => setFormData({ ...formData, preferredTerm: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Örn: Etkinlik, Gelişim, Kazanım..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yasaklı Terimler (virgülle ayırın)
                </label>
                <input
                  type="text"
                  value={formData.bannedTerms}
                  onChange={(e) => setFormData({ ...formData, bannedTerms: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Activity, aktivite, etkinlik activity..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  AI bu terimleri kullanmayacak ve tercih edilen terimle değiştirecek
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Eş Anlamlılar / Kabul Edilenler (virgülle ayırın)
                </label>
                <input
                  type="text"
                  value={formData.synonyms}
                  onChange={(e) => setFormData({ ...formData, synonyms: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Kabul edilen alternatif terimler..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tanım</label>
                <textarea
                  value={formData.definition}
                  onChange={(e) => setFormData({ ...formData, definition: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Terimin kısa tanımı..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {categoryOptions.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.preferredTerm}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">CSV Dosyası İçe Aktar</h3>
              <button onClick={() => setShowImportModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                CSV dosyası şu sütunları içermelidir:<br/>
                <code className="text-xs bg-gray-100 px-1 rounded">preferredTerm, synonyms, bannedTerms, definition, category</code>
              </p>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImport}
                  className="hidden"
                  id="glossary-csv-upload"
                />
                <label
                  htmlFor="glossary-csv-upload"
                  className="cursor-pointer text-amber-600 hover:text-amber-700 font-medium"
                >
                  CSV dosyası seçin
                </label>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlossaryAdmin;
